import { useState } from "react";
import { Check, Copy, UserPlus, X } from "lucide-react";
import {
  useCancelBookingInvite,
  useListInvitesForBooking,
} from "@/lib/api";
import { useToast } from "@/components/ux/Toast";
import { UserAPI } from "@shared/types/api";
import tracking from "@/lib/tracking";
import { InviteFriendsModal } from "./InviteFriendsModal";
import { Trans, useT } from "@/lib/i18n";

interface Props {
  bookingId: string;
  /**
   * Whether the booking is still within its cancellation window. Drives the
   * cancel affordance on existing connections — outside the window, both
   * parties are locked into the link.
   */
  allowInviting?: boolean;
  /** Event type for the booking — passed through to the invite modal so its
   *  copy reads naturally for brunch / dinner / drinks / coffee. */
  eventType?: string | null;
}

const ACTIVE_STATUSES: UserAPI.BookingInviteStatus[] = ["pending", "accepted"];

/**
 * "+1" management surface on a booking card.
 *
 * - "Going with X" chips for accepted invites (both directions).
 * - Compact roster of pending invites with copy/cancel.
 * - "Add +1" button opens a modal with two paths: pick from your
 *   prior dinner connections, or send by email.
 */
export function InvitePlusOneSection({
  bookingId,
  allowInviting,
  eventType,
}: Props) {
  const { t } = useT();
  const toast = useToast();
  const { data, isLoading, refetch } = useListInvitesForBooking(bookingId);
  const cancel = useCancelBookingInvite();

  const [modalOpen, setModalOpen] = useState(false);

  const invites = data?.data || [];
  const visibleInvites = invites.filter((i) =>
    ACTIVE_STATUSES.includes(i.status),
  );
  const receivedAccepted = visibleInvites.filter(
    (i) => i.direction === "received" && i.status === "accepted",
  );
  const sentAccepted = visibleInvites.filter(
    (i) => i.direction !== "received" && i.status === "accepted",
  );
  const pendingInvites = visibleInvites.filter((i) => i.status === "pending");

  // Pass these to the modal so we can grey-out connections / emails we've
  // already reached out to from this booking.
  const alreadyInvitedUserIds = invites
    .filter((i) => ACTIVE_STATUSES.includes(i.status) && i.inviteeUserId)
    .map((i) => i.inviteeUserId!) as string[];
  const alreadyInvitedEmails = invites
    .filter((i) => ACTIVE_STATUSES.includes(i.status) && i.email)
    .map((i) => i.email!) as string[];

  const handleCopy = async (inviteId: string, shareUrl?: string) => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      tracking.inviteLinkCopied(inviteId, "list");
      toast.success(t("invites.section.linkCopied"));
    } catch {
      toast.error(t("invites.section.failedToCopy"));
    }
  };

  const handleCancel = async (inviteId: string, previousStatus: string) => {
    tracking.inviteCancelClicked(inviteId, previousStatus);
    try {
      await cancel.mutateAsync(inviteId);
      refetch();
    } catch (e: any) {
      toast.error(e?.message || t("invites.section.failedToCancel"));
    }
  };

  const renderInviteRow = (inv: UserAPI.BookingInvite) => {
    const isReceived = inv.direction === "received";
    const isAccepted = inv.status === "accepted";
    const otherParty = isReceived ? inv.inviter : inv.invitee;
    const label = otherParty
      ? `${otherParty.firstName}${otherParty.lastName ? " " + otherParty.lastName : ""}`
      : inv.email || "Unknown";
    const subline = isReceived
      ? isAccepted
        ? t("invites.section.goingWithYou")
        : t("invites.section.pending")
      : isAccepted
        ? t("invites.section.joined")
        : t("invites.section.pending");
    const cancelAllowed = !isAccepted || allowInviting;
    const cancelTitle = !isAccepted
      ? isReceived
        ? t("invites.section.cancelTitles.declineInvite")
        : t("invites.section.cancelTitles.cancelInvite")
      : allowInviting
        ? isReceived
          ? t("invites.section.cancelTitles.leaveTheirPlusOne")
          : t("invites.section.cancelTitles.removePlusOne")
        : t("invites.section.cancelTitles.windowClosed");
    return (
      <div
        key={inv.id}
        className="flex items-center justify-between gap-2 text-sm bg-base-200/50 rounded px-3 py-2"
      >
        <div className="min-w-0 flex-1">
          <div className="truncate">{label}</div>
          <div className="text-xs text-dark-muted">{subline}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isAccepted && !isReceived && inv.shareUrl && (
            <button
              onClick={() => handleCopy(inv.id, inv.shareUrl)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 border border-black/10 dark:border-white/10 text-xs font-medium text-neutral-700 dark:text-neutral-200 transition-colors"
              title={t("invites.section.copyInviteLink")}
            >
              <Copy className="w-3 h-3" />
              {t("invites.section.copyLink")}
            </button>
          )}
          <button
            onClick={() =>
              cancelAllowed && handleCancel(inv.id, inv.status)
            }
            disabled={!cancelAllowed}
            className="p-1.5 rounded text-base-content/60 hover:bg-base-content/10 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-base-content/60"
            title={cancelTitle}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  // Build a single list of accepted-connection display entries so we can
  // render them with one pass (and pull other-party initials for avatars).
  const acceptedConnections = [
    ...receivedAccepted.map((inv) => ({
      id: inv.id,
      firstName: inv.inviter.firstName,
      lastName: inv.inviter.lastName,
    })),
    ...sentAccepted.map((inv) => ({
      id: inv.id,
      firstName: inv.invitee?.firstName || inv.email || "?",
      lastName: inv.invitee?.lastName || null,
    })),
  ];

  const getInitials = (firstName: string, lastName: string | null) => {
    const a = firstName.charAt(0).toUpperCase();
    const b = lastName ? lastName.charAt(0).toUpperCase() : "";
    return `${a}${b}` || "?";
  };

  if (!allowInviting && acceptedConnections.length == 0) {
    return <></>
  }

  return (
    <div className="mt-4 pt-4 border-t border-divider">
      {/* Accepted-connection chips — bigger and crisper. Avatar circle with
          initials, name, and a check badge. Always visible since this is
          a status, not a pending action. */}
      {acceptedConnections.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {acceptedConnections.map((c) => (
            <div
              key={c.id}
              className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 border border-black/10 dark:border-white/10 text-sm"
            >
              <div className="relative">
                <div className="w-7 h-7 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                  {getInitials(c.firstName, c.lastName)}
                </div>
                {/* Check badge stays the accent primary green so the "going"
                    state still reads as a positive confirmation at a glance. */}
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-primary-500 border-2 border-white dark:border-neutral-900 flex items-center justify-center">
                  <Check className="w-2 h-2 text-white" strokeWidth={3} />
                </div>
              </div>
              <span className="font-medium leading-none text-neutral-800 dark:text-neutral-100">
                <Trans
                  i18nKey="invites.section.goingWith"
                  values={{ name: c.firstName }}
                  components={{ strong: <span className="font-semibold" /> }}
                />
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Pending invites — keep visible so the sender can copy/cancel without
          opening the modal. */}
      {pendingInvites.length > 0 && (
        <div className="space-y-2 mb-3">
          {pendingInvites.map(renderInviteRow)}
        </div>
      )}

      {!isLoading && allowInviting && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            tracking.inviteSectionToggled(bookingId, true);
            setModalOpen(true);
          }}
          className="group w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-primary-500/5 hover:bg-primary-500/10 transition-colors"
        >
          <div className="w-9 h-9 rounded-full bg-primary-500/15 group-hover:bg-primary-500/25 flex items-center justify-center transition-colors">
            <UserPlus className="w-5 h-5 text-primary-500" strokeWidth={2.5} />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-primary-600 dark:text-primary-400 leading-tight">
              {t("invites.section.addPlusOne")}
            </div>
            <div className="text-xs text-dark-muted">
              {t("invites.section.inviteAFriend")}
            </div>
          </div>
        </button>
      )}

      <InviteFriendsModal
        isOpen={modalOpen}
        bookingId={bookingId}
        eventType={eventType}
        alreadyInvitedUserIds={alreadyInvitedUserIds}
        alreadyInvitedEmails={alreadyInvitedEmails}
        onClose={() => setModalOpen(false)}
        onSent={() => refetch()}
      />
    </div>
  );
}
